import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  addressMonitoringSelectors,
  showDemoDataSelectors,
} from '@/redux/selectors';
import { AddressMonitoringData } from '@/redux/models';
import { addressMonitoringActions } from '@/redux/actions';
import {
  AddressMonitoringListRequest,
  MonitoringUnsubscribeRequest,
} from '@/api';
import {
  AlertBox,
  Button,
  HistoryEmpty,
  Loader,
  SectionBox,
} from '@/components';
import { useTranslation } from 'react-i18next';
import AnimateHeight, { Height } from 'react-animate-height';
import { Popover } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import { HistoryAddressHeader } from '../HistoryAddressHeader';
import { HistoryAddressItem } from '../HistoryAddressItem';

import { addressHistoryDemoData } from './AddressHistory.constants';
import styles from './AddressHistory.module.sass';

const AddressHistory = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { loading, error, data } = useAppSelector(
    addressMonitoringSelectors.getAddressMonitoringSlice,
  );
  const [loaderHeight, setLoaderHeight] = useState<Height>(0);

  const isShowDemoData = useAppSelector(showDemoDataSelectors.byMonitoring);

  const addressHistoryData = useMemo((): AddressMonitoringData | null => {
    return isShowDemoData ? addressHistoryDemoData : data;
  }, [isShowDemoData, data]);

  useEffect(() => {
    if (!addressHistoryData && !isShowDemoData) {
      const payload: AddressMonitoringListRequest = {};
      dispatch(addressMonitoringActions.fetchMonitoringList(payload));
    }
  }, [addressHistoryData, isShowDemoData]);

  useEffect(() => {
    setLoaderHeight(loading ? 'auto' : 0);
  }, [loading]);

  const isEmpty = !addressHistoryData || addressHistoryData.items.length === 0;
  const sectionTitle = t('translation.monitoring_table_title');
  const emptyHistoryTitle = t('translation.empty_monitoring_history_title');
  const emptyHistoryDesc = t(
    'translation.empty_monitoring_history_description',
  );

  const onUnsubscribe = (id: number) => {
    const payload: MonitoringUnsubscribeRequest = { id };
    dispatch(addressMonitoringActions.unsubscribe(payload));
  };
  const isShowMoreAvailable =
    !!addressHistoryData &&
    addressHistoryData.totalCount - addressHistoryData.items.length > 0;

  const showMoreHandler = useCallback(() => {
    if (addressHistoryData) {
      const itemsToLoadTotal =
        addressHistoryData.totalCount - addressHistoryData.items.length;

      if (itemsToLoadTotal > 0) {
        const currentPage = Math.ceil(
          addressHistoryData.items.length / addressHistoryData.pageLimit,
        );

        const payload: AddressMonitoringListRequest = {
          // backend counts pages from 0
          page: currentPage,
        };

        dispatch(addressMonitoringActions.fetchMonitoringList(payload));
      }
    }
  }, [dispatch, data]);

  const refreshHandler = () => {
    const payload: AddressMonitoringListRequest = {};
    dispatch(addressMonitoringActions.fetchMonitoringList(payload));
  };

  return (
    <SectionBox>
      {{
        headerClassName: styles.sectionHeader,
        title: sectionTitle,
        actions: (
          <Popover content={t('translation.refresh_btn')}>
            <span className={`${styles.iconButton}`} onClick={refreshHandler}>
              <ReloadOutlined />
            </span>
          </Popover>
        ),
        body: (
          <>
            {!!error && (
              <div className={styles.error}>
                <AlertBox type="danger">
                  {{
                    title: t('translation.ooops'),
                    description: error,
                  }}
                </AlertBox>
              </div>
            )}
            <AnimateHeight
              className={styles.loader}
              duration={100}
              height={loaderHeight}
            >
              <Loader />
            </AnimateHeight>

            {isEmpty && (
              <HistoryEmpty title={emptyHistoryTitle} desc={emptyHistoryDesc} />
            )}

            {!isEmpty && (
              <>
                <HistoryAddressHeader />
                {addressHistoryData?.items.map((item) => (
                  <HistoryAddressItem
                    key={item.id}
                    item={item}
                    loading={loading}
                    isShowDemoData={isShowDemoData}
                    onUnsubscribe={onUnsubscribe}
                  />
                ))}

                {isShowMoreAvailable && (
                  <div className={styles.show}>
                    <Button
                      size="m"
                      onClick={showMoreHandler}
                      disabled={loading}
                      nowrap
                    >
                      {t('translation.show_more')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        ),
      }}
    </SectionBox>
  );
};

export default AddressHistory;
